import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { guardarSala } from '../lib/sala';

/**
 * Retorno del OAuth de Google. El backend ya dejó el refresh en cookie httpOnly
 * y redirige aquí con ?ok=1 (o ?ok=0&reason=...). Intercambiamos la cookie por
 * un access token (hydrate) y enrutamos según el rol; si falló, volvemos al login
 * con un mensaje humano.
 */
const RAZONES: Record<string, string> = {
  google_error: 'Google rechazó el ingreso. Probá de nuevo.',
  faltan_datos: 'Google no devolvió los datos necesarios.',
  state_expirado: 'El intento tardó demasiado. Volvé a ingresar con Google.',
  state_invalido: 'No pudimos validar el retorno de Google. Reintentá.',
  intercambio: 'No pudimos completar el ingreso con Google. Reintentá.',
};

export function AuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const hydrate = useAuthStore((s) => s.hydrate);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const corrio = useRef(false);

  useEffect(() => {
    if (corrio.current) return;
    corrio.current = true;

    const ok = params.get('ok');
    if (ok === '0') {
      const reason = params.get('reason') ?? '';
      setMensaje(RAZONES[reason] ?? 'No pudimos ingresar con Google.');
      const t = setTimeout(() => navigate('/login', { replace: true }), 2200);
      return () => clearTimeout(t);
    }

    // QR → Google → sala: el backend ya dejó la membresía y nos dice en qué
    // proceso Y en qué día. EL CÓDIGO MANDA también por esta vía; el nombre de
    // la sala lo completa después useSesionActiva con las participaciones.
    //
    // El día importa tanto como el proceso: acá se guardaba `sesion_id: null` y
    // se tiraba el día que traía el QR. Como todas las sesiones del sprint nacen
    // «preparada», la heurística caía a «la primera no cerrada» y mandaba a
    // todos al día 1 — el QR del día 3 llevaba a la actividad del día 1.
    const proceso = params.get('proceso');
    if (proceso) {
      guardarSala({
        proceso_id: proceso,
        proceso_nombre: '',
        org_nombre: '',
        sesion_id: params.get('sesion'),
        sesion_fecha: params.get('fecha'),
        autoregistro: false,
      });
    }

    void (async () => {
      try {
        await hydrate();
      } catch {
        // hydrate ya deja status='anonymous' ante un fallo; no dejamos el
        // spinner colgado — caemos al login abajo.
      }
      const { status } = useAuthStore.getState();
      // '/entrar' (RootRedirect) enruta por ROL: participante -> /app (Bolsillo),
      // conductor -> /procesos. Ir a '/' caía en la LandingPage y el participante
      // veía la portada comercial en vez de su sala («no pasa nada»).
      navigate(status === 'authenticated' ? '/entrar' : '/login', { replace: true });
    })();
  }, [params, hydrate, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base px-6 text-center">
      <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
      <p className="font-sans text-sm text-content-muted">
        {mensaje ?? 'Estamos ingresando a ÁGORA…'}
      </p>
    </div>
  );
}
